// SPEC-465 M1 — SVG path 解析保真：arc-flag 归一化 + 纯 JS 解析/展平（单一 SoT，零依赖）。
//
// 根因（生产任务 pmuw1ngb29yh8，leaf 图标墨迹越 cell 压穿「芭芭农场」标签）：
// Konva 9.3.22 parsePathData 对 SVG compact arc-flag 误 tokenize —— `a10 10 0 0010-10` 中
// `0010-10`（laf=0 sf=0 x=10 y=-10）被数字正则吞成 `10,-10`，弧参数错位 → 巨弧拖尾。
// 全库 348 枚 iconData 扫描命中 5 枚：leaf/contact/door-open/face-angry/face-slightly-frowning
// （检出器 = tools 侧 konva parse + 本文件 segmentsBBox；阳性对照记录见 SPEC-465 PR body）。
//
// 修复 = 类级机制（禁逐图标 patch）：渲染边界（Artboard.tsx icon Path）对 path data 调
// normalizePathData() 把 arc-flag 展开为空格分隔规范形（compact `0010-10` → `0 0 10 -10`），
// 对任何 SVG path 来源生效（iconData / 用户导入 / agent 生成）；幂等、对规范形 path 字节稳定、
// 非法形状 fail-safe 原样返回（渲染行为永不比修复前更差）。
//
// 本文件同时是 path 数学 SoT：parsePathSegments 输出与 Konva parsePathData 兼容的 segment 形状
// （{command,points,start}，A = 中心参数化 [cx,cy,rx,ry,theta,dTheta,psi,fs]，弧度制 psi），
// segmentsBBox 对 Konva parse 与本文件 parse 统一展平取 bbox —— M1 检出器 / 全库回归 /
// M3 像素审计（geometry.ts，worker 经相对 import 复用 auditGeometry 同款惯例）共用一套数学。
// arcCenter() 与 Konva convertEndpointToCenterParameterization 逐运算镜像（浮点位级一致，
// 单测 pin 两解析器对归一化数据 deep-equal）。

/** SVG path 命令 → 参数元数（A/a 每组第 4、5 位为单字符 arc-flag，见 tokenizePath）。 */
const CMD_ARITY: Readonly<Record<string, number>> = {
  M: 2, m: 2, Z: 0, z: 0, L: 2, l: 2, H: 1, h: 1, V: 1, v: 1,
  C: 6, c: 6, S: 4, s: 4, Q: 4, q: 4, T: 2, t: 2, A: 7, a: 7,
};

/** SVG 1.1 数字语法（sticky；`1.2.3` 依语法切分为 `1.2` + `.3`）。 */
const NUM_RE = /[-+]?(?:\d+\.\d+|\d+|\.\d+)(?:[eE][-+]?\d+)?/y;
const SEP_RE = /[\s,]/;

function isCmdChar(c: string | undefined): boolean {
  return c !== undefined && Object.prototype.hasOwnProperty.call(CMD_ARITY, c);
}

interface CmdTokens {
  cmd: string;
  /** 参数组（隐式重复 = 多组）；元素为原始数字/旗标子串（字节保留，不做值改写）。 */
  groups: string[][];
}

/**
 * arc-flag 感知 tokenizer。每命令按元数切组；A/a 每组第 4、5 位恰取一字符（'0'|'1'），
 * 其余按 SVG 数字语法。任何非法形状（未知字符 / 参数不足 / 旗标非 0|1 / Z 带参）→ null，
 * 由调用方 fail-safe。
 */
function tokenizePath(d: string): CmdTokens[] | null {
  const out: CmdTokens[] = [];
  const n = d.length;
  let i = 0;
  while (i < n) {
    while (i < n && SEP_RE.test(d[i])) i++;
    if (i >= n) break;
    const cmd = d[i];
    if (!isCmdChar(cmd)) return null;
    i++;
    const arity = CMD_ARITY[cmd];
    const isArc = cmd === 'A' || cmd === 'a';
    const groups: string[][] = [];
    for (;;) {
      let j = i;
      while (j < n && SEP_RE.test(d[j])) j++;
      if (j >= n || isCmdChar(d[j])) { i = j; break; }
      if (arity === 0) return null;
      i = j;
      const g: string[] = [];
      for (let k = 0; k < arity; k++) {
        while (i < n && SEP_RE.test(d[i])) i++;
        if (isArc && (k === 3 || k === 4)) {
          const c = d[i];
          if (c !== '0' && c !== '1') return null;
          g.push(c);
          i++;
        } else {
          NUM_RE.lastIndex = i;
          const m = NUM_RE.exec(d);
          if (!m) return null;
          g.push(m[0]);
          i += m[0].length;
        }
      }
      groups.push(g);
    }
    out.push({ cmd, groups });
  }
  return out;
}

/** 规范序列化：命令字母 + 空格分隔参数（组间/命令间单空格；数字与旗标保留原始子串）。 */
function serializeTokens(tokens: CmdTokens[]): string {
  const parts: string[] = [];
  for (const t of tokens) {
    let s = t.cmd;
    for (const g of t.groups) s += (s.length > 1 ? ' ' : '') + g.join(' ');
    parts.push(s);
  }
  return parts.join(' ');
}

const NORM_CACHE_MAX = 4096;
const normCache = new Map<string, string>();

/**
 * path data 归一化（渲染边界唯一入口）：arc-flag 展开为空格分隔规范形，其余 token 字节保留。
 * 幂等；规范形输入字节稳定（如 Artboard link badge `M0 5 H10 M6 1 L10 5 L6 9` 原样返回）；
 * 非法形状 fail-safe 原样返回。带容量上限缓存（渲染热路径每帧调用，348 枚内置 + 用户 path）。
 */
export function normalizePathData(d: string): string {
  if (!d) return d;
  const hit = normCache.get(d);
  if (hit !== undefined) return hit;
  const tokens = tokenizePath(d);
  const out = tokens === null ? d : serializeTokens(tokens);
  if (normCache.size >= NORM_CACHE_MAX) normCache.clear();
  normCache.set(d, out);
  return out;
}

/** Konva parsePathData 兼容 segment（A = 中心参数化 [cx,cy,rx,ry,theta,dTheta,psi(rad),fs]）。 */
export interface PathSegment {
  command: 'M' | 'L' | 'C' | 'Q' | 'A' | 'z';
  points: number[];
  start: { x: number; y: number } | undefined;
}

/**
 * 端点弧 → 中心参数化。与 Konva 9.3.22 Path.convertEndpointToCenterParameterization
 * 逐运算镜像（含 lambda 半径放大、fa===fs 取负、isNaN(f)→0、fs 半程修正），
 * 保证两解析器对同一归一化 token 流输出位级一致的浮点。
 */
function arcCenter(
  x1: number, y1: number, x2: number, y2: number,
  fa: number, fs: number, rxIn: number, ryIn: number, psiDeg: number,
): number[] {
  let rx = rxIn;
  let ry = ryIn;
  const psi = psiDeg * (Math.PI / 180.0);
  const xp = (Math.cos(psi) * (x1 - x2)) / 2.0 + (Math.sin(psi) * (y1 - y2)) / 2.0;
  const yp = (-1 * Math.sin(psi) * (x1 - x2)) / 2.0 + (Math.cos(psi) * (y1 - y2)) / 2.0;
  const lambda = (xp * xp) / (rx * rx) + (yp * yp) / (ry * ry);
  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }
  let f = Math.sqrt((rx * rx * (ry * ry) - rx * rx * (yp * yp) - ry * ry * (xp * xp)) /
    (rx * rx * (yp * yp) + ry * ry * (xp * xp)));
  if (fa === fs) {
    f *= -1;
  }
  if (isNaN(f)) {
    f = 0;
  }
  const cxp = (f * rx * yp) / ry;
  const cyp = (f * -ry * xp) / rx;
  const cx = (x1 + x2) / 2.0 + Math.cos(psi) * cxp - Math.sin(psi) * cyp;
  const cy = (y1 + y2) / 2.0 + Math.sin(psi) * cxp + Math.cos(psi) * cyp;
  const vMag = (v: number[]): number => Math.sqrt(v[0] * v[0] + v[1] * v[1]);
  const vRatio = (u: number[], v: number[]): number =>
    (u[0] * v[0] + u[1] * v[1]) / (vMag(u) * vMag(v));
  const vAngle = (u: number[], v: number[]): number =>
    (u[0] * v[1] < u[1] * v[0] ? -1 : 1) * Math.acos(vRatio(u, v));
  const theta = vAngle([1, 0], [(xp - cxp) / rx, (yp - cyp) / ry]);
  const u = [(xp - cxp) / rx, (yp - cyp) / ry];
  const v = [(-1 * xp - cxp) / rx, (-1 * yp - cyp) / ry];
  let dTheta = vAngle(u, v);
  if (vRatio(u, v) <= -1) {
    dTheta = Math.PI;
  }
  if (vRatio(u, v) >= 1) {
    dTheta = 0;
  }
  if (fs === 0 && dTheta > 0) {
    dTheta = dTheta - 2 * Math.PI;
  }
  if (fs === 1 && dTheta < 0) {
    dTheta = dTheta + 2 * Math.PI;
  }
  return [cx, cy, rx, ry, theta, dTheta, psi, fs];
}

/**
 * 纯 JS path 解析（arc-flag 感知，语义与 SVG 1.1 一致）：相对→绝对、H/V→L、
 * M 隐式重复→L、S/T 反射控制点、A→中心参数化。segment 形状与 Konva parsePathData 兼容
 * （单测 pin：348 枚 + badge 上两解析器 deep-equal）。非法形状 → null（审计侧 fail-closed）。
 * z 后当前点归子路径起点（SVG spec；Konva 在合法数据上行为一致，单测覆盖）。
 */
export function parsePathSegments(d: string): PathSegment[] | null {
  const tokens = tokenizePath(d);
  if (tokens === null) return null;
  const segs: PathSegment[] = [];
  let cpx = 0;
  let cpy = 0;
  for (const t of tokens) {
    const up = t.cmd.toUpperCase();
    const rel = t.cmd !== up;
    if (up === 'Z') {
      // 逐字镜像 Konva 9.3.22 parsePathData：z 不重置当前点（停留在闭合前终点）。
      // SVG spec「z 后当前点=子路径起点」Konva 仅在紧随其后的相对 m 用 backward-scan 近似
      // （见下方 M 分支）；渲染即 truth —— 本文件契约是与渲染器位级一致，非 SVG 教科书语义。
      // （实证：iconData `scale` 含 `…a5 5 0 0 1-6 0zV7`，spec 语义 V7 x=31 vs Konva 渲染 x=28。）
      segs.push({ command: 'z', points: [], start: undefined });
      continue;
    }
    for (let gi = 0; gi < t.groups.length; gi++) {
      const g = t.groups[gi].map(parseFloat);
      const start = { x: cpx, y: cpy };
      const ax = (k: number): number => (rel ? cpx + g[k] : g[k]);
      const ay = (k: number): number => (rel ? cpy + g[k] : g[k]);
      switch (up) {
        case 'M': {
          let x = ax(0);
          let y = ay(1);
          // 镜像 Konva m-after-z：segs.length>2 && 末段=z → backward-scan 最近 'M' 段，
          // 锚点取其 points+delta（≈子路径起点语义）；scan 无命中保持普通相对锚 —— 与 Konva 逐分支一致。
          if (rel && gi === 0 && segs.length > 2 && segs[segs.length - 1].command === 'z') {
            for (let i = segs.length - 2; i >= 0; i--) {
              if (segs[i].command === 'M') {
                x = segs[i].points[0] + g[0];
                y = segs[i].points[1] + g[1];
                break;
              }
            }
          }
          segs.push({ command: gi === 0 ? 'M' : 'L', points: [x, y], start });
          cpx = x;
          cpy = y;
          break;
        }
        case 'L': {
          const x = ax(0);
          const y = ay(1);
          segs.push({ command: 'L', points: [x, y], start });
          cpx = x;
          cpy = y;
          break;
        }
        case 'H': {
          const x = ax(0);
          segs.push({ command: 'L', points: [x, cpy], start });
          cpx = x;
          break;
        }
        case 'V': {
          const y = ay(0);
          segs.push({ command: 'L', points: [cpx, y], start });
          cpy = y;
          break;
        }
        case 'C': {
          const x1 = ax(0);
          const y1 = ay(1);
          const x2 = ax(2);
          const y2 = ay(3);
          const x = ax(4);
          const y = ay(5);
          segs.push({ command: 'C', points: [x1, y1, x2, y2, x, y], start });
          cpx = x;
          cpy = y;
          break;
        }
        case 'S': {
          const prev = segs[segs.length - 1];
          let cx1 = cpx;
          let cy1 = cpy;
          if (prev && prev.command === 'C') {
            cx1 = cpx + (cpx - prev.points[2]);
            cy1 = cpy + (cpy - prev.points[3]);
          }
          const x2 = ax(0);
          const y2 = ay(1);
          const x = ax(2);
          const y = ay(3);
          segs.push({ command: 'C', points: [cx1, cy1, x2, y2, x, y], start });
          cpx = x;
          cpy = y;
          break;
        }
        case 'Q': {
          const x1 = ax(0);
          const y1 = ay(1);
          const x = ax(2);
          const y = ay(3);
          segs.push({ command: 'Q', points: [x1, y1, x, y], start });
          cpx = x;
          cpy = y;
          break;
        }
        case 'T': {
          const prev = segs[segs.length - 1];
          let cx1 = cpx;
          let cy1 = cpy;
          if (prev && prev.command === 'Q') {
            cx1 = cpx + (cpx - prev.points[0]);
            cy1 = cpy + (cpy - prev.points[1]);
          }
          const x = ax(0);
          const y = ay(1);
          segs.push({ command: 'Q', points: [cx1, cy1, x, y], start });
          cpx = x;
          cpy = y;
          break;
        }
        case 'A': {
          const rx = g[0];
          const ry = g[1];
          const psi = g[2];
          const fa = g[3];
          const fs = g[4];
          const x = ax(5);
          const y = ay(6);
          segs.push({
            command: 'A',
            points: arcCenter(cpx, cpy, x, y, fa, fs, rx, ry, psi),
            start,
          });
          cpx = x;
          cpy = y;
          break;
        }
        default:
          return null;
      }
    }
  }
  return segs;
}

export interface PathBBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  /** 任一 segment 含非有限坐标（解析损坏/参数不足）——bbox 不可信，审计侧按越界处理（fail-closed）。 */
  degenerate: boolean;
}

const CUBIC_SAMPLES = 32;

function sampleCubic(
  x0: number, y0: number, x1: number, y1: number,
  x2: number, y2: number, x3: number, y3: number,
  out: { x: number; y: number }[],
): void {
  for (let i = 1; i < CUBIC_SAMPLES; i++) {
    const t = i / CUBIC_SAMPLES;
    const mt = 1 - t;
    const a = mt * mt * mt;
    const b = 3 * mt * mt * t;
    const c = 3 * mt * t * t;
    const e = t * t * t;
    out.push({
      x: a * x0 + b * x1 + c * x2 + e * x3,
      y: a * y0 + b * y1 + c * y2 + e * y3,
    });
  }
}

function sampleQuad(
  x0: number, y0: number, x1: number, y1: number, x2: number, y2: number,
  out: { x: number; y: number }[],
): void {
  for (let i = 1; i < CUBIC_SAMPLES; i++) {
    const t = i / CUBIC_SAMPLES;
    const mt = 1 - t;
    out.push({
      x: mt * mt * x0 + 2 * mt * t * x1 + t * t * x2,
      y: mt * mt * y0 + 2 * mt * t * y1 + t * t * y2,
    });
  }
}

/** 中心参数化椭圆弧采样（与 Konva sceneFunc translate/rotate/scale+arc 数学等价）。 */
function sampleArc(points: number[], out: { x: number; y: number }[]): void {
  const [cx, cy, rx, ry, theta, dTheta, psi] = points;
  if (![cx, cy, rx, ry, theta, dTheta, psi].every(Number.isFinite)) {
    out.push({ x: NaN, y: NaN });
    return;
  }
  const n = Math.min(256, Math.max(32, Math.ceil(Math.abs(dTheta) / (Math.PI / 32))));
  const cosPsi = Math.cos(psi);
  const sinPsi = Math.sin(psi);
  for (let i = 0; i <= n; i++) {
    const t = theta + (dTheta * i) / n;
    const cosT = Math.cos(t) ;
    const sinT = Math.sin(t);
    out.push({
      x: cx + rx * cosT * cosPsi - ry * sinT * sinPsi,
      y: cy + rx * cosT * sinPsi + ry * sinT * cosPsi,
    });
  }
}

/**
 * segments → 几何 bbox（曲线/弧展平采样；对 Konva parsePathData 与本文件 parsePathSegments
 * 的输出统一适用 —— 同一数学 SoT）。空 segments → null。任何非有限坐标 → degenerate=true
 * （调用方 fail-closed：degenerate 视同越界）。
 */
export function segmentsBBox(segments: PathSegment[]): PathBBox | null {
  if (segments.length === 0) return null;
  const pts: { x: number; y: number }[] = [];
  for (const s of segments) {
    const sx = s.start ? s.start.x : NaN;
    const sy = s.start ? s.start.y : NaN;
    const p = s.points;
    switch (s.command) {
      case 'M':
      case 'L':
        pts.push({ x: p[0], y: p[1] });
        break;
      case 'C':
        pts.push({ x: sx, y: sy }, { x: p[4], y: p[5] });
        sampleCubic(sx, sy, p[0], p[1], p[2], p[3], p[4], p[5], pts);
        break;
      case 'Q':
        pts.push({ x: sx, y: sy }, { x: p[2], y: p[3] });
        sampleQuad(sx, sy, p[0], p[1], p[2], p[3], pts);
        break;
      case 'A':
        pts.push({ x: sx, y: sy });
        sampleArc(p, pts);
        break;
      case 'z':
        if (Number.isFinite(sx)) pts.push({ x: sx, y: sy });
        break;
    }
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let degenerate = false;
  for (const q of pts) {
    if (!Number.isFinite(q.x) || !Number.isFinite(q.y)) {
      degenerate = true;
      continue;
    }
    if (q.x < minX) minX = q.x;
    if (q.x > maxX) maxX = q.x;
    if (q.y < minY) minY = q.y;
    if (q.y > maxY) maxY = q.y;
  }
  if (!degenerate && pts.length > 0 && minX === Infinity) degenerate = true;
  return { minX, minY, maxX, maxY, degenerate };
}

/**
 * 便捷入口：path data（任意来源/任意写法）→ 几何 bbox。内部走 arc-flag 感知 tokenizer，
 * 因此对未归一化的 compact 写法同样给出**正确 SVG 语义**的 bbox（Konva 则需先
 * normalizePathData 才与其一致 —— 这正是 M1 检出器的判别面）。非法形状 → null。
 */
export function pathGeometryBBox(d: string): PathBBox | null {
  const segs = parsePathSegments(d);
  if (segs === null) return null;
  return segmentsBBox(segs);
}
